import { useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

function getDeviceType(): string {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

export function useRadioTracking() {
  const currentSessionId = useRef<string | null>(null);
  const sessionStart = useRef<number>(0);

  const startSession = useCallback(async (station: {
    stationuuid: string;
    name: string;
    country?: string;
    countrycode?: string;
    tags?: string;
    bitrate?: number;
    language?: string;
  }, searchQuery?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return; // only track authenticated users

      const { data, error } = await supabase
        .from('radio_sessions' as any)
        .insert({
          user_id: user.id,
          station_uuid: station.stationuuid,
          station_name: station.name,
          station_country: station.country || null,
          station_country_code: station.countrycode || null,
          station_tags: station.tags || null,
          station_bitrate: station.bitrate || null,
          station_language: station.language || null,
          source_page: window.location.pathname,
          search_query: searchQuery || null,
          device_type: getDeviceType(),
        })
        .select('id')
        .single();

      if (!error && data) {
        currentSessionId.current = (data as any).id;
        sessionStart.current = Date.now();
      }
    } catch {
      // silent fail - don't break radio for analytics
    }
  }, []);

  const endSession = useCallback(async () => {
    if (!currentSessionId.current) return;
    
    const duration = Math.round((Date.now() - sessionStart.current) / 1000);
    
    try {
      await supabase
        .from('radio_sessions' as any)
        .update({
          ended_at: new Date().toISOString(),
          duration_seconds: duration,
        } as any)
        .eq('id', currentSessionId.current);
    } catch {
      // silent
    }
    
    currentSessionId.current = null;
    sessionStart.current = 0;
  }, []);

  const markConversion = useCallback(async (type: 'signup' | 'topup' | 'call') => {
    if (!currentSessionId.current) return;
    
    const field = type === 'signup' ? 'converted_to_signup' 
      : type === 'topup' ? 'converted_to_topup' 
      : 'converted_to_call';
    
    try {
      await supabase
        .from('radio_sessions' as any)
        .update({ [field]: true } as any)
        .eq('id', currentSessionId.current);
    } catch {
      // silent
    }
  }, []);

  return { startSession, endSession, markConversion };
}
